import React, { FC, useMemo } from 'react';
import { observer } from 'mobx-react';
import { useStore } from '../../../../store/store-provider';
import classnames from 'classnames';
import QuestionsReviewModeNavTabs from './questions-review-mode-nav-tabs/questions-review-mode-nav-tabs';
import { QnadaReviewModeTabs, QuestionState } from '../../../../constans/question-enums';
import QuestionsReviewModeTabContainer from './questions-review-mode-tab-container/questions-review-mode-tab-container';
import {
  getNewPrioritize,
  getNewPrioritizeByDnDMove,
  sortByPrioritize,
} from '../../../../utils/helpers/prioritize-helpers';
import { DragDropContext, DraggableLocation, DropResult } from 'react-beautiful-dnd';
import QuestionEntity, { TQuestionEntity } from '../../../../store/question-store/question-entetiy';
import { MobileTabContainer } from '../../../index';
import { getGMTDate } from '../../../../utils/helpers/date-helper';
import { getMixpanelService } from '../../../../utils/services/mixpanel-service-instance';
import { MIXPANEL_EVENT_IDENTIFIERS, MIXPANEL_EVENT_TYPES } from '../../../../constans/mixpanel-contants';
import { NonceCSP } from '../../../../constans/common-constants';


type TAdditionalHandler = (id: string | null, stateType: QuestionState, setActiveMessageId: (id: string | null) => void) => void;

export type TReviewModeAdditionalAction = {
  additionalHandler: TAdditionalHandler;
  stateType: QuestionState;
}

interface IProps {
  title?: string;
  questions: QuestionEntity[];
  subTitle?: string;
  isMobile: boolean;
}

export interface IReviewNavTabs {
  title: QnadaReviewModeTabs;
  questions: QuestionEntity[];
}

const QuestionReviewModeContainer: FC<IProps> = observer(({ questions, isMobile }): JSX.Element => {
  const { questionStore } = useStore();
  const {
    updateQuestion,
    isDisabled,
    activeReviewModeTabOnMobile,
    setActiveReviewModeTabOnMobile,
    updateOrderReviewMode,
    updateMoveReviewModeDnd,
    getManageQuestionNewLabel
  } = questionStore;

  const mobileTabsCls = 'question-container question-container--review bg-white';
  const isEmptyMsg = questions.length;
  const reviewModeChangeQuestionStateHandler: TAdditionalHandler = (id, stateType, setActiveMessageId) => {
    updateQuestion(id, stateType, getGMTDate());
    setActiveMessageId(id);

    getMixpanelService().track(MIXPANEL_EVENT_IDENTIFIERS.QATab, MIXPANEL_EVENT_TYPES.MOVED_BY_ARROW);
  };

  const numberOfReviewNewQuestions = getManageQuestionNewLabel().onSubscribeNewQuestionStatus(QuestionState.REVIEW);

  const reviewList = questions.filter((q) => q.state === QuestionState.REVIEW);
  const prioritizedList = questions.filter((q) => q.state === QuestionState.PRIORITIZED);
  const presenterList = questions.filter((q) => q.state === QuestionState.PRESENTER);

  const navTabs: IReviewNavTabs[] = [
    {
      title: QnadaReviewModeTabs.FOR_REVIEW,
      questions: reviewList
    },
    {
      title: QnadaReviewModeTabs.PRIORITIZED,
      questions: prioritizedList
    },
    {
      title: QnadaReviewModeTabs.PRESENTER,
      questions: presenterList
    }
  ];

  const getSource = (stateType: string): TQuestionEntity[] => {
    switch (stateType){
      case QuestionState.REVIEW:
        return reviewList;
      case QuestionState.PRIORITIZED:
        return prioritizedList;
      case QuestionState.PRESENTER:
        return presenterList;
      default:
        return [];
    }
  };

  const reorder = (startIndex: number, endIndex: number, source: TQuestionEntity[]): void => {
    if(startIndex === endIndex) return;

    const order: number = getNewPrioritize({
      startIndex,
      endIndex,
      list: source
    });

    updateOrderReviewMode(source[startIndex], order);
  };

  const move = (
    source: TQuestionEntity[],
    destination: TQuestionEntity[],
    droppableSource: DraggableLocation,
    droppableDestination: DraggableLocation): void => {
    const order: number = getNewPrioritizeByDnDMove(
      source,
      destination,
      droppableSource.index,
      droppableDestination.index
    );

    updateMoveReviewModeDnd(source[droppableSource.index], droppableDestination.droppableId, order);
  };

  const onDragEnd = ({ destination, source }: DropResult) => {
    if (!destination) return;

    if(source.droppableId === destination.droppableId) {
      reorder(
        source.index,
        destination.index,
        getSource(source.droppableId)
      );
    } else {
      move(
        getSource(source.droppableId),
        getSource(destination.droppableId),
        source,
        destination
      );
    }
  };
  return (
    <div
      className={classnames('wrapper wrapper--review-mode', {
        'pos-border-right': activeReviewModeTabOnMobile === QnadaReviewModeTabs.FOR_REVIEW,
        'pos-border-left pos-border-right': activeReviewModeTabOnMobile === QnadaReviewModeTabs.PRIORITIZED,
        'pos-border-left ': activeReviewModeTabOnMobile === QnadaReviewModeTabs.PRESENTER
      })}
    >
      <DragDropContext onDragEnd={onDragEnd} nonce={NonceCSP.DND}>
        <MobileTabContainer classes={mobileTabsCls} isSelected={activeReviewModeTabOnMobile === QnadaReviewModeTabs.FOR_REVIEW}>
          {useMemo(() => {
            return (
              <QuestionsReviewModeTabContainer
                title={QnadaReviewModeTabs.FOR_REVIEW}
                questions={sortByPrioritize(reviewList) as QuestionEntity[]}
                reviewModeAdditionalAction={{
                  additionalHandler: reviewModeChangeQuestionStateHandler,
                  stateType: QuestionState.PRIORITIZED,
                }}
                numberOfNewQuestions={numberOfReviewNewQuestions}
                isSpecialActionButton
                droppableId={QuestionState.REVIEW}
                isEmptyMsg={isEmptyMsg}
                updateQuestion={updateQuestion}
                isDisabled={isDisabled}
              />
            );
          }, [reviewList])}
        </MobileTabContainer>
        <MobileTabContainer classes={mobileTabsCls} isSelected={activeReviewModeTabOnMobile === QnadaReviewModeTabs.PRIORITIZED}>
          {useMemo(() => {
            return (
              <QuestionsReviewModeTabContainer
                title={QnadaReviewModeTabs.PRIORITIZED}
                questions={sortByPrioritize(prioritizedList) as QuestionEntity[]}
                reviewModeAdditionalAction={{
                  additionalHandler: reviewModeChangeQuestionStateHandler,
                  stateType: QuestionState.PRESENTER,
                }}
                droppableId={QuestionState.PRIORITIZED}
                updateQuestion={updateQuestion}
                isDisabled={isDisabled}
              />
            );
          }, [prioritizedList])}
        </MobileTabContainer>
        <MobileTabContainer classes={mobileTabsCls} isSelected={activeReviewModeTabOnMobile === QnadaReviewModeTabs.PRESENTER}>
          {useMemo(() => {
            return(
              <QuestionsReviewModeTabContainer
                title={QnadaReviewModeTabs.PRESENTER}
                questions={sortByPrioritize(presenterList) as QuestionEntity[]}
                droppableId={QuestionState.PRESENTER}
                updateQuestion={updateQuestion}
                isDisabled={isDisabled}
              />
            );
          }, [presenterList])}
        </MobileTabContainer>
      </DragDropContext>
      {useMemo(() => {
        return (
          <div className="questions-column-pagination">
            <QuestionsReviewModeNavTabs
              isMobile={isMobile}
              tabs={navTabs}
              selected={activeReviewModeTabOnMobile}
              setSelected={setActiveReviewModeTabOnMobile}
            />
          </div>
        );
      }, [activeReviewModeTabOnMobile, isMobile])}
    </div>
  );
});

export default QuestionReviewModeContainer;
